// D:\directiveai-react\frontend\src\components\AGTEdu\components\FileUploader.js

import React, { useState, useRef } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Upload,
  Check,
  AlertTriangle,
  X,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileMinus
} from 'lucide-react';
import agtEduService from '../services/agtEduService';

// Hidden component for file input
const HiddenInput = styled('input')({
  display: 'none'
});

// Styled component for the drag and drop zone
const DropZone = styled(Box)(({ theme, isDragging, colors }) => ({
  border: `2px dashed ${isDragging ? colors.accent : colors.borderColor}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  backgroundColor: isDragging ? `${colors.accent}10` : colors.backgroundTertiary,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: `${colors.accent}10`,
    borderColor: colors.accent
  }
}));

// Function to get the icon based on file type
const getFileIcon = (fileType) => {
  if (fileType?.includes('pdf')) {
    return <FileMinus size={20} />;
  } else if (fileType?.includes('spreadsheet') || fileType?.includes('excel') || fileType?.includes('csv')) {
    return <FileSpreadsheet size={20} />;
  } else if (fileType?.includes('image')) {
    return <FileImage size={20} />;
  } else {
    return <FileText size={20} />;
  }
};

/**
 * Component for uploading files to a subject
 */
const FileUploader = ({ subjectId, onUploadComplete, colors }) => {
  const [open, setOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const fileInputRef = useRef();

  // Dialog open handler
  const handleOpen = () => {
    setOpen(true);
    setFiles([]);
    setUploadResults([]);
    setUploading(false);
    setUploadProgress({});
  };

  // Dialog close handler
  const handleClose = () => {
    setOpen(false);
    // If there are successfully uploaded files, notify the parent component
    if (uploadResults.some(result => result.success)) {
      onUploadComplete && onUploadComplete();
    }
  };

  // File selection handler
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    if (selectedFiles.length > 0) {
      setFiles(prev => [...prev, ...selectedFiles]);
    }
    
    // Clear the input to allow selecting the same file multiple times
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    
    if (droppedFiles.length > 0) {
      setFiles(prev => [...prev, ...droppedFiles]);
    }
  };

  // Remove a file from the list
  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    
    // If there are already upload results, remove them as well
    if (uploadResults.length > 0) {
      setUploadResults(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Upload files to the server
  const handleUpload = async () => {
    if (files.length === 0 || !subjectId) return;
    
    setUploading(true);
    const results = [];
    
    // Upload each file individually for better control
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Create a FormData for this file
        const formData = new FormData();
        formData.append('file', file);
        
        // Initialize progress for this file
        setUploadProgress(prev => ({
          ...prev,
          [i]: 0
        }));
        
        // Upload the file with progress tracking
        const response = await agtEduService.uploadFile(
          subjectId, 
          formData, 
          (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(prev => ({
              ...prev,
              [i]: progress
            }));
          }
        );
        
        // Record successful result
        results.push({
          fileName: file.name,
          success: true,
          fileId: response.data.id
        });
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        
        // Record error
        results.push({
          fileName: file.name,
          success: false,
          error: error.message || 'Unknown error'
        });
      }
    }
    
    // Update results
    setUploadResults(results);
    setUploading(false);
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<Upload size={16} color="white" />}
        sx={{
          textTransform: 'none',
          borderRadius: 999,
          backgroundColor: colors.accent,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: colors.accent
          }
        }}
      >
        Upload files
      </Button>

      <Dialog 
        open={open} 
        onClose={uploading ? undefined : handleClose}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: colors.background
          }
        }}
      >
        <DialogTitle sx={{ color: colors.textDefault }}>
          Upload files to the subject
        </DialogTitle>
        
        <DialogContent>
          {/* Drag and drop zone */}
          <DropZone
            colors={colors}
            isDragging={isDragging}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{ mb: 3 }}
          >
            <Upload 
              size={40} 
              style={{ 
                color: isDragging ? colors.accent : colors.textSecondary,
                marginBottom: 16
              }} 
            />
            <Typography 
              variant="body1" 
              sx={{ 
                color: colors.textDefault,
                textAlign: 'center' 
              }}
            >
              Drag and drop files here
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.textSecondary,
                textAlign: 'center',
                mt: 1
              }}
            >
              Or click to select files
            </Typography>
            
            <HiddenInput
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
            />
          </DropZone>
          
          {/* List of selected files */}
          {files.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: colors.textDefault }}>
                {files.length} file{files.length !== 1 ? 's' : ''} selected
              </Typography>
              
              <List sx={{ bgcolor: colors.backgroundTertiary, borderRadius: 2 }}>
                {files.map((file, index) => (
                  <ListItem 
                    key={`${file.name}-${index}`}
                    secondaryAction={
                      !uploading && (
                        <IconButton 
                          edge="end" 
                          onClick={() => handleRemoveFile(index)}
                          sx={{ color: colors.textSecondary }}
                        >
                          <X size={16} />
                        </IconButton>
                      )
                    }
                    sx={{
                      borderBottom: `1px solid ${colors.borderColor}`,
                      '&:last-child': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    <ListItemIcon>
                      {getFileIcon(file.type)}
                    </ListItemIcon>
                    <ListItemText 
                      primary={file.name} 
                      secondary={`${(file.size / 1024).toFixed(1)} KB`}
                      primaryTypographyProps={{
                        color: colors.textDefault,
                        variant: 'body2',
                        noWrap: true,
                        sx: { maxWidth: '200px' }
                      }}
                      secondaryTypographyProps={{
                        color: colors.textSecondary,
                        variant: 'caption'
                      }}
                    />
                    
                    {/* Show progress or result if uploading or already uploaded */}
                    {(uploading || uploadResults.length > 0) && (
                      <Box sx={{ ml: 2, minWidth: '100px', display: 'flex', alignItems: 'center' }}>
                        {uploading && uploadProgress[index] !== undefined && (
                          <>
                            <LinearProgress 
                              variant="determinate" 
                              value={uploadProgress[index]} 
                              sx={{ 
                                flexGrow: 1,
                                mr: 1,
                                height: 6,
                                borderRadius: 3
                              }}
                            />
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              {uploadProgress[index]}%
                            </Typography>
                          </>
                        )}
                        
                        {!uploading && uploadResults[index] && (
                          uploadResults[index].success ? (
                            <Tooltip title="Successfully uploaded">
                              <Check size={18} style={{ color: colors.success }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title={uploadResults[index].error || "Error uploading"}>
                              <AlertTriangle size={18} style={{ color: colors.error }} />
                            </Tooltip>
                          )
                        )}
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {/* Message if no files are selected */}
          {files.length === 0 && uploadResults.length === 0 && (
            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center', 
                color: colors.textSecondary,
                my: 2
              }}
            >
              No files selected
            </Typography>
          )}
          
          {/* Results summary after upload */}
          {!uploading && uploadResults.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: colors.backgroundTertiary, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: colors.textDefault }}>
                Results:
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  <Check size={14} style={{ color: colors.success, marginRight: 4, verticalAlign: 'middle' }} />
                  {uploadResults.filter(r => r.success).length} file(s) uploaded
                </Typography>
                
                {uploadResults.some(r => !r.success) && (
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    <AlertTriangle size={14} style={{ color: colors.error, marginRight: 4, verticalAlign: 'middle' }} />
                    {uploadResults.filter(r => !r.success).length} with errors
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClose}
            disabled={uploading}
            sx={{
              color: colors.textSecondary,
              textTransform: 'none'
            }}
          >
            {uploadResults.length > 0 ? 'Close' : 'Cancel'}
          </Button>
          
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || files.length === 0 || uploadResults.length > 0}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
            sx={{
              backgroundColor: colors.accent,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: colors.accent
              }
            }}
          >
            {uploading ? 'Uploading...' : 'Upload files'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileUploader;